import React from 'react';
import { Outlet } from 'react-router-dom';
import {
  Backdrop,
  Box,
  CircularProgress,
  Drawer,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { ContentBreadcrumbs } from '../components/ContentBreadcrumbs';
import { LayoutFooter } from '../components/LayoutFooter';
import { LayoutHeader } from '../components/LayoutHeader';
import { NavDrawerContent } from '../components/NavDrawerContent';
import { useAppUi } from '../context/AppUiContext';
import { DRAWER_WIDTH_PX, NAV_SHELL_MAX_HEIGHT, mainContentScrollSx } from './drawerConstants';
import {
  LAYOUT_CONTENT_BG,
  LAYOUT_FOOTER_RESERVED_PX,
  LAYOUT_MAIN_GUTTER_PX,
  LAYOUT_NAV_BG,
  LAYOUT_SURFACE,
} from './layoutColors';

export function AppShellLayout() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { navDrawerOpen, setNavDrawerOpen } = useAppUi();
  const { impersonatePending } = useAuth();

  const closeDrawer = () => setNavDrawerOpen(false);

  const footerLeftPx = isMdUp && navDrawerOpen ? DRAWER_WIDTH_PX : 0;

  const navShellSx = {
    width: DRAWER_WIDTH_PX,
    flexShrink: 0,
    alignSelf: 'stretch',
    bgcolor: LAYOUT_NAV_BG,
    height: '100%',
    maxHeight: NAV_SHELL_MAX_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: LAYOUT_CONTENT_BG,
        overflow: 'hidden',
      }}
    >
      <LayoutHeader />
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          width: '100%',
          alignItems: 'stretch',
          overflow: 'hidden',
        }}
      >
        {isMdUp && navDrawerOpen && (
          <Box component="nav" id="app-nav-drawer" aria-label="Main navigation" sx={navShellSx}>
            <NavDrawerContent />
          </Box>
        )}
        {!isMdUp && (
          <Drawer
            anchor="left"
            open={navDrawerOpen}
            onClose={closeDrawer}
            PaperProps={{
              sx: {
                width: DRAWER_WIDTH_PX,
                boxSizing: 'border-box',
                bgcolor: LAYOUT_NAV_BG,
                borderRight: 'none',
              },
            }}
            ModalProps={{ keepMounted: true }}
          >
            <Box
              component="nav"
              id="app-nav-drawer"
              aria-label="Main navigation"
              sx={{ height: '100%', bgcolor: LAYOUT_NAV_BG, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
              <NavDrawerContent onNavigate={closeDrawer} />
            </Box>
          </Drawer>
        )}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            component="section"
            aria-label="Main content"
            sx={{
              flex: 1,
              minHeight: 0,
              bgcolor: LAYOUT_CONTENT_BG,
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              px: `${LAYOUT_MAIN_GUTTER_PX}px`,
              pt: { xs: 0.5, sm: 1 },
              pb: (t) => `calc(${LAYOUT_FOOTER_RESERVED_PX}px + ${t.spacing(0.5)})`,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                bgcolor: LAYOUT_SURFACE,
                boxSizing: 'border-box',
                px: { xs: 1.25, sm: 1.5 },
                py: { xs: 0.75, sm: 1 },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <ContentBreadcrumbs />
              <Box sx={mainContentScrollSx}>
                <Outlet />
              </Box>
            </Box>
          </Box>
          <LayoutFooter leftOffsetPx={footerLeftPx} />
        </Box>
      </Box>
      <Backdrop
        open={impersonatePending}
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <Stack alignItems="center" spacing={2} data-testid="impersonate-loading">
          <CircularProgress color="inherit" />
          <Typography variant="body2">Switching user…</Typography>
        </Stack>
      </Backdrop>
    </Box>
  );
}
